/**
 * Geometry-free vision config schema primitives.
 *
 * These zod schemas are shared between routine-runner's vision layer
 * (`LocateTypes`, `VisionInterface`) and the skill-config layer in `@sb/ai`.
 * They intentionally live in their own leaf module that imports ONLY `zod`.
 *
 * `LocateTypes` and `VisionInterface` both pull in `@sb/geometry` (for
 * `CartesianPose`), which transitively requires `three` at module-load time.
 * Lightweight backend services such as `updater-bot` import `@sb/ai` but do
 * not bundle `three`, so any runtime edge from `@sb/ai` into those modules
 * crashes with `Cannot find module 'three'`. Keeping these primitives here
 * lets `@sb/ai` consume them without dragging in geometry/three.
 *
 * `LocateTypes` and `VisionInterface` re-export these names, so existing
 * importers are unaffected.
 */
import * as zod from 'zod';

export const ResultOrdering = zod.enum([
  // Scan order (corner-based diagonal sweep)
  'top-left-to-bottom-right',
  'top-right-to-bottom-left',
  'bottom-left-to-top-right',
  'bottom-right-to-top-left',
  // By property (always descending)
  'confidence-desc',
  'area-desc',
]);

export type ResultOrdering = zod.infer<typeof ResultOrdering>;

// DinoFast detection method primitives
export const DinoFastPrecision = zod.enum(['fast', 'standard', 'fine']);

export type DinoFastPrecision = zod.infer<typeof DinoFastPrecision>;

export const DinoFastReferenceEmbedding = zod.object({
  values: zod.array(zod.number()),
});

export type DinoFastReferenceEmbedding = zod.infer<
  typeof DinoFastReferenceEmbedding
>;

export const RegionOfInterest = zod.object({
  top: zod.number(),
  bottom: zod.number(),
  left: zod.number(),
  right: zod.number(),
});

export type RegionOfInterest = zod.infer<typeof RegionOfInterest>;

export const DEFAULT_REGION_OF_INTEREST: RegionOfInterest = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

// Shared 3D-pose-estimation config. Consumed by both routine-runner's
// `Detection3DParams` (which adds 2D-detection fields) and the skill-config
// layer's `ProjectionConfig.detection3d` in `@sb/ai`. Geometry-free (zod only).
// primitiveDimX/Y/Z and fpfhVoxelSize are in METERS. cadModelUnits is separate:
// it records the unit system of an uploaded CAD mesh file, not the units of
// fields here.
export const Detection3DPoseEstimationConfig = zod.object({
  poseMethod: zod
    .enum(['OBB_DEPTH', 'OBB_ICP', 'FPFH_ICP'])
    .default('OBB_DEPTH'),
  cadSource: zod.enum(['NONE', 'PRIMITIVE', 'CAD_FILE']).default('NONE'),
  primitiveShapeType: zod.enum(['BOX', 'CYLINDER']).default('BOX'),
  // Primitive dimensions are in METERS and feed the vision-bot pose RPC; a
  // zero/negative/Infinite dimension is a degenerate primitive, so bound them
  // (a bare `z.number()` would accept all three).
  primitiveDimX: zod.number().positive().finite().default(0.1),
  primitiveDimY: zod.number().positive().finite().default(0.1),
  primitiveDimZ: zod.number().positive().finite().default(0.1),
  cadModelStorageID: zod.string().optional(),
  cadModelFileName: zod.string().optional(),
  cadModelFormat: zod.enum(['STL', 'OBJ', 'PLY']).optional(),
  cadModelUnits: zod
    .enum(['METERS', 'MILLIMETERS', 'CENTIMETERS', 'INCHES'])
    .default('MILLIMETERS'),
  confidenceThreshold: zod.number().min(0).max(1).default(0.5),
  maxDetections: zod.number().int().positive().default(10),
  depthOutlierPercentile: zod.number().min(0).max(100).default(2),
  // Minimum ICP / RANSAC inlier-ratio (0–1) required to publish a 3D pose.
  // Detections below this threshold are dropped (not just hidden), so
  // downstream motion and UI overlays never see a low-quality fit. 0
  // disables filtering entirely. The default 0.5 drops clearly-wrong poses
  // (~8 mm+ alignment error) across OBB_ICP and FPFH_ICP; for strict
  // FPFH-with-multi-start use cases, 0.7–0.8 is typical. OBB_DEPTH does
  // not report a fit quality, so the filter is a no-op for that method.
  minInlierRatio: zod.number().min(0).max(1).default(0.5),
  // FPFH_ICP only: number of RANSAC+ICP attempts per crop. The estimator
  // keeps the one with highest ICP fitness (bad-basin rate ≈ p_bad^N).
  // Higher values trade latency for reliability. Ignored by other methods.
  fpfhRansacRestarts: zod.number().int().positive().default(4),
  // FPFH_ICP only: voxel size (meters) for scene+ref downsampling and
  // FPFH feature support. Small parts (<5 cm) prefer 0.001–0.002; larger
  // parts (>20 cm) prefer 0.005–0.01. Ignored by other methods.
  fpfhVoxelSize: zod.number().positive().finite().default(0.003),
});

export type Detection3DPoseEstimationConfig = zod.infer<
  typeof Detection3DPoseEstimationConfig
>;
